import { useEffect, useRef, useState } from 'react';
import { dataUsageTracker, type DataUsageSnapshot } from '@/lib/dataUsageTracker';

/**
 * Subscribe to near-real-time data-usage snapshots for the admin monitor.
 *
 * Polls the in-memory tracker on a throttled interval (default 1s) only while
 * `active` — so a closed/non-admin monitor costs nothing. Also derives an
 * instantaneous transfer rate from successive snapshots.
 */
export function useDataUsageSnapshot(
  active: boolean,
  intervalMs = 1000,
): { snapshot: DataUsageSnapshot; rateBytesPerSec: number } {
  const [snapshot, setSnapshot] = useState<DataUsageSnapshot>(() => dataUsageTracker.getSnapshot());
  const [rateBytesPerSec, setRate] = useState(0);
  const prevRef = useRef({ bytes: snapshot.totalWireBytes, t: Date.now() });

  useEffect(() => {
    if (!active) return;
    const tick = () => {
      const snap = dataUsageTracker.getSnapshot();
      const now = Date.now();
      const dt = (now - prevRef.current.t) / 1000;
      if (dt > 0) {
        setRate(Math.max(0, (snap.totalWireBytes - prevRef.current.bytes) / dt));
      }
      prevRef.current = { bytes: snap.totalWireBytes, t: now };
      setSnapshot(snap);
    };
    tick();
    const id = setInterval(tick, intervalMs);
    return () => clearInterval(id);
  }, [active, intervalMs]);

  return { snapshot, rateBytesPerSec };
}
